import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import PinIcon from './PinIcon';

// Canvas component handles all drawing and pin logic
const Canvas = forwardRef(function Canvas({ isVideo, videoUrl, image, currentTool, color, pins, setPins, showPinModal, setShowPinModal, selectedPin, setSelectedPin, shapes, setShapes, placingPin, setPlacingPin, pendingPlacingPin, setPendingPlacingPin, thickness, width, height, showTextModal, setShowTextModal, setTextPosition, selectedText, setSelectedText, draggingText, setDraggingText, handleTextDrag, handleTextEdit, handleTextDelete, handleTextRotation }, ref) {
  const canvasRef = useRef(null);
  // Drawing state for shapes and freehand
  const [drawing, setDrawing] = useState(false);
  const [lastPoint, setLastPoint] = useState(null);
  // Preview states for shape drawing
  const [previewRect, setPreviewRect] = useState(null);
  const [previewCircle, setPreviewCircle] = useState(null);
  const [previewLine, setPreviewLine] = useState(null);
  const [previewArrow, setPreviewArrow] = useState(null);
  const [freehandPoints, setFreehandPoints] = useState([]);
  const [selectedShapeIndex, setSelectedShapeIndex] = useState(null);
  const [dragOffset, setDragOffset] = useState(null);
  const [shapeRotation, setShapeRotation] = useState(0);

  // Expose the canvas DOM node to parent for saving
  useImperativeHandle(ref, () => canvasRef.current);

  // Reset drawing/preview state when tool or image changes
  useEffect(() => {
    setDrawing(false);
    setLastPoint(null);
    setPreviewRect(null);
    setPreviewCircle(null);
    setPreviewLine(null);
    setPreviewArrow(null);
    setFreehandPoints([]);
  }, [currentTool, image]);

  // When a shape is selected, update the rotation state
  useEffect(() => {
    if (selectedShapeIndex !== null && shapes[selectedShapeIndex]) {
      setShapeRotation(shapes[selectedShapeIndex].rotation || 0);
    }
  }, [selectedShapeIndex, shapes]);

  // Get mouse position relative to canvas
  const getCanvasOffset = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  // Handle mouse down for drawing tools
  const handleMouseDown = (e) => {
    if ([
      'freehand',
      'square',
      'circle',
      'line',
      'arrow',
    ].includes(currentTool)) {
      setDrawing(true);
      setLastPoint(getCanvasOffset(e));
      if (currentTool === 'freehand') setFreehandPoints([getCanvasOffset(e)]);
      if (currentTool === 'square') setPreviewRect(null);
      if (currentTool === 'circle') setPreviewCircle(null);
      if (currentTool === 'line') setPreviewLine(null);
      if (currentTool === 'arrow') setPreviewArrow(null);
    } else if (currentTool === 'drag') {
      // Check if a shape is clicked
      const pos = getCanvasOffset(e);
      let found = false;
      for (let i = shapes.length - 1; i >= 0; i--) {
        if (isPointInShape(pos, shapes[i])) {
          setSelectedShapeIndex(i);
          setDragOffset({
            x: pos.x - getShapeCenter(shapes[i]).x,
            y: pos.y - getShapeCenter(shapes[i]).y,
          });
          found = true;
          break;
        }
      }
      if (!found) {
        setSelectedShapeIndex(null);
        setDragOffset(null);
      }
    }
  };

  // Handle mouse move for drawing/preview
  const handleMouseMove = (e) => {
    if (currentTool === 'drag' && canvasRef.current) {
      const pos = getCanvasOffset(e);
      const overShape = shapes.some(shape => isPointInShape(pos, shape));
      const overText = shapes.some(shape => shape.type === 'text' && isTextHit(pos, shape));
      
      if (draggingText) {
        canvasRef.current.style.cursor = 'grabbing';
      } else {
        canvasRef.current.style.cursor = (overShape || overText) ? 'pointer' : 'default';
      }
    }
    
    if (draggingText && selectedText) {
      // Handle text dragging - update position in real-time
      const pos = getCanvasOffset(e);
      setShapes(shapes => shapes.map(shape => 
        shape.id === selectedText.id 
          ? { ...shape, x: pos.x, y: pos.y }
          : shape
      ));
      return;
    }
    
    if (!drawing && currentTool === 'drag' && selectedShapeIndex !== null && dragOffset) {
      // Dragging selected shape
      const pos = getCanvasOffset(e);
      setShapes(shapes => shapes.map((shape, idx) => {
        if (idx !== selectedShapeIndex) return shape;
        return moveShape(shape, pos, dragOffset);
      }));
      return;
    }
    if (!drawing) return;
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (currentTool === 'freehand') {
      // Draw freehand lines
      ctx.strokeStyle = color;
      ctx.lineWidth = thickness * 10;
      ctx.lineCap = 'round';
      const newPoint = getCanvasOffset(e);
      if (lastPoint) {
        ctx.beginPath();
        ctx.moveTo(lastPoint.x, lastPoint.y);
        ctx.lineTo(newPoint.x, newPoint.y);
        ctx.stroke();
      }
      setLastPoint(newPoint);
      setFreehandPoints(points => [...points, newPoint]);
    } else if (currentTool === 'square') {
      // Preview square
      const start = lastPoint;
      const end = getCanvasOffset(e);
      setPreviewRect({ start, end });
      redrawCanvas();
      drawPreviewSquare(start, end);
    } else if (currentTool === 'circle') {
      // Preview circle
      const start = lastPoint;
      const end = getCanvasOffset(e);
      setPreviewCircle({ start, end });
      redrawCanvas();
      drawPreviewCircle(start, end);
    } else if (currentTool === 'line') {
      // Preview line
      const start = lastPoint;
      const end = getCanvasOffset(e);
      setPreviewLine({ start, end });
      redrawCanvas();
      drawPreviewLine(start, end);
    } else if (currentTool === 'arrow') {
      // Preview arrow
      const start = lastPoint;
      const end = getCanvasOffset(e);
      setPreviewArrow({ start, end });
      redrawCanvas();
      drawPreviewArrow(start, end);
    }
  };

  // Handle mouse up to finish drawing
  const handleMouseUp = (e) => {
    if (draggingText) {
      setDraggingText(false);
      // Update selectedText position to match the new position
      if (selectedText) {
        const pos = getCanvasOffset(e);
        setSelectedText({ ...selectedText, x: pos.x, y: pos.y });
      }
      return;
    }
    if (!drawing && currentTool === 'drag' && selectedShapeIndex !== null) {
      setDragOffset(null);
      return;
    }
    if (!drawing) return;
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (currentTool === 'freehand') {
      setDrawing(false);
      setLastPoint(null);
      setShapes([...shapes, { type: 'freehand', points: freehandPoints, color, thickness }]);
      setFreehandPoints([]);
    } else if (currentTool === 'square') {
      // Draw final square
      const start = lastPoint;
      const end = getCanvasOffset(e);
      drawSquare(ctx, start, end, color, thickness);
      setDrawing(false);
      setLastPoint(null);
      setPreviewRect(null);
      setShapes([...shapes, { type: 'square', start, end, color, thickness }]);
    } else if (currentTool === 'circle') {
      // Draw final circle
      const start = lastPoint;
      const end = getCanvasOffset(e);
      drawCircle(ctx, start, end, color, thickness);
      setDrawing(false);
      setLastPoint(null);
      setPreviewCircle(null);
      setShapes([...shapes, { type: 'circle', start, end, color, thickness }]);
    } else if (currentTool === 'line') {
      // Draw final line
      const start = lastPoint;
      const end = getCanvasOffset(e);
      drawLine(ctx, start, end, color, thickness);
      setDrawing(false);
      setLastPoint(null);
      setPreviewLine(null);
      setShapes([...shapes, { type: 'line', start, end, color, thickness }]);
    } else if (currentTool === 'arrow') {
      // Draw final arrow
      const start = lastPoint;
      const end = getCanvasOffset(e);
      drawArrow(ctx, start, end, color, thickness);
      setDrawing(false);
      setLastPoint(null);
      setPreviewArrow(null);
      setShapes([...shapes, { type: 'arrow', start, end, color, thickness }]);
    }
  };

  // Check if click is on text
  const isTextHit = (pos, textShape) => {
    if (!textShape.text) return false;
    
    // Create a temporary canvas to measure text
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.font = `${textShape.fontWeight || 'normal'} ${textShape.fontSize || 16}px sans-serif`;
    
    const lines = textShape.text.split('\n');
    const lineHeight = (textShape.fontSize || 16) * 1.2;
    const maxWidth = Math.max(...lines.map(line => tempCtx.measureText(line).width));
    const totalHeight = lines.length * lineHeight;
    
    // Add some padding for easier clicking
    const padding = 8;
    
    return pos.x >= textShape.x - padding && 
           pos.x <= textShape.x + maxWidth + padding && 
           pos.y >= textShape.y - padding && 
           pos.y <= textShape.y + totalHeight + padding;
  };



  // Handle click for pin placement and selection
  const handleCanvasClick = (e) => {
    const pos = getCanvasOffset(e);
    
    if (placingPin) {
      // Place the pin at the clicked location
      const newPin = { ...placingPin, x: pos.x, y: pos.y, id: Date.now() };
      console.log('Placing new pin:', newPin);
      setPins(currentPins => {
        const updatedPins = [...currentPins, newPin];
        console.log('Updated pins after placement:', updatedPins);
        return updatedPins;
      });
      // Do NOT clear placingPin here, so user can add multiple pins
      return;
    }
    
    if (currentTool === 'text') {
      // Check if clicking on existing text
      const clickedText = shapes.find(shape => shape.type === 'text' && isTextHit(pos, shape));
      if (clickedText) {
        handleTextEdit(clickedText);
      } else {
        // Open text modal at clicked position
        setTextPosition(pos);
        setShowTextModal(true);
      }
      return;
    }
    
    if (currentTool === 'drag') {
      // Check if clicking on text
      const clickedText = shapes.find(shape => shape.type === 'text' && isTextHit(pos, shape));
      if (clickedText) {
        // Only select text if not already selected, don't start dragging automatically
        if (!selectedText || selectedText.id !== clickedText.id) {
          setSelectedText(clickedText);
          setDraggingText(false);
        }
      } else {
        setSelectedText(null);
        setDraggingText(false);
      }
    }
  };

  // Redraw canvas (used for previews and persistent shapes)
  const redrawCanvas = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Draw all persistent shapes
    shapes.forEach(shape => {
      if (shape.type === 'freehand') drawFreehand(ctx, shape.points, shape.color, shape.thickness, shape.rotation);
      if (shape.type === 'square') drawSquare(ctx, shape.start, shape.end, shape.color, shape.thickness, shape.rotation);
      if (shape.type === 'circle') drawCircle(ctx, shape.start, shape.end, shape.color, shape.thickness, shape.rotation);
      if (shape.type === 'line') drawLine(ctx, shape.start, shape.end, shape.color, shape.thickness, shape.rotation);
      if (shape.type === 'arrow') drawArrow(ctx, shape.start, shape.end, shape.color, shape.thickness, shape.rotation);
    });
  };

  // Drawing helpers for shapes and previews
  // Update rotation of selected shape
  const handleRotationChange = (angle) => {
    setShapeRotation(angle);
    setShapes(shapes => shapes.map((shape, idx) => {
      if (idx !== selectedShapeIndex) return shape;
      return { ...shape, rotation: angle };
    }));
  };

  // Update all draw functions to apply rotation if present
  function withRotation(ctx, center, angle, drawFn) {
    ctx.save();
    ctx.translate(center.x, center.y);
    ctx.rotate((angle || 0) * Math.PI / 180);
    ctx.translate(-center.x, -center.y);
    drawFn();
    ctx.restore();
  }

  const drawSquare = (ctx, start, end, color, thick, rotation) => {
    if (!start || !end) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = (thick || thickness) * 10;
    const x = start.x;
    const y = start.y;
    const w = end.x - start.x;
    const h = end.y - start.y;
    const center = { x: x + w / 2, y: y + h / 2 };
    withRotation(ctx, center, rotation, () => {
      ctx.strokeRect(x, y, w, h);
    });
  };
  const drawPreviewSquare = (start, end, rotation) => {
    if (!start || !end) return;
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.save();
    ctx.setLineDash([6, 4]);
    drawSquare(ctx, start, end, color, thickness, rotation);
    ctx.restore();
  };
  const drawCircle = (ctx, start, end, color, thick, rotation) => {
    if (!start || !end) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = (thick || thickness) * 10;
    const x = (start.x + end.x) / 2;
    const y = (start.y + end.y) / 2;
    const rx = Math.abs(end.x - start.x) / 2;
    const ry = Math.abs(end.y - start.y) / 2;
    const center = { x, y };
    withRotation(ctx, center, rotation, () => {
      ctx.beginPath();
      ctx.ellipse(x, y, rx, ry, 0, 0, 2 * Math.PI);
      ctx.stroke();
    });
  };
  const drawPreviewCircle = (start, end, rotation) => {
    if (!start || !end) return;
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.save();
    ctx.setLineDash([6, 4]);
    drawCircle(ctx, start, end, color, thickness, rotation);
    ctx.restore();
  };
  const drawLine = (ctx, start, end, color, thick, rotation) => {
    if (!start || !end) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = (thick || thickness) * 10;
    const center = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
    withRotation(ctx, center, rotation, () => {
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
    });
  };
  const drawPreviewLine = (start, end, rotation) => {
    if (!start || !end) return;
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.save();
    ctx.setLineDash([6, 4]);
    drawLine(ctx, start, end, color, thickness, rotation);
    ctx.restore();
  };
  const drawArrow = (ctx, start, end, color, thick, rotation) => {
    if (!start || !end) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = (thick || thickness) * 10;
    const center = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
    withRotation(ctx, center, rotation, () => {
      // Draw main line
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
      // Draw arrowhead
      const headlen = 14; // length of head in pixels
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const angle = Math.atan2(dy, dx);
      ctx.beginPath();
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(end.x - headlen * Math.cos(angle - Math.PI / 6), end.y - headlen * Math.sin(angle - Math.PI / 6));
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(end.x - headlen * Math.cos(angle + Math.PI / 6), end.y - headlen * Math.sin(angle + Math.PI / 6));
      ctx.stroke();
    });
  };
  const drawPreviewArrow = (start, end, rotation) => {
    if (!start || !end) return;
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.save();
    ctx.setLineDash([6, 4]);
    drawArrow(ctx, start, end, color, thickness, rotation);
    ctx.restore();
  };

  // Helper to draw freehand lines
  const drawFreehand = (ctx, points, color, thick, rotation) => {
    if (!points || points.length < 2) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = (thick || thickness) * 10;
    ctx.lineCap = 'round';
    // Use centroid as center
    const n = points.length;
    const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
    const center = { x: sum.x / n, y: sum.y / n };
    withRotation(ctx, center, rotation, () => {
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.stroke();
    });
  };

  // Helper to draw text
  const drawText = (ctx, textShape) => {
    if (!textShape.text) return;
    ctx.save();
    
    // Apply rotation if specified
    if (textShape.rotation && textShape.rotation !== 0) {
      ctx.translate(textShape.x, textShape.y);
      ctx.rotate((textShape.rotation * Math.PI) / 180);
      ctx.translate(-textShape.x, -textShape.y);
    }
    
    ctx.fillStyle = textShape.color || color;
    ctx.font = `${textShape.fontWeight || 'normal'} ${textShape.fontSize || 16}px sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    
    // Add text shadow for better visibility
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    
    // Split text by lines and draw each line
    const lines = textShape.text.split('\n');
    const lineHeight = (textShape.fontSize || 16) * 1.2;
    const maxWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
    const totalHeight = lines.length * lineHeight;
    
    lines.forEach((line, index) => {
      ctx.fillText(line, textShape.x, textShape.y + (index * lineHeight));
    });
    
    ctx.restore();
  };

  // Add keyboard support for text operations
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedText && e.key === 'Delete') {
        handleTextDelete(selectedText);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedText, handleTextDelete]);

  // Comprehensive canvas redraw effect - handles shapes, pins, and previews
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear the canvas first
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw all persistent shapes first
    shapes.forEach(shape => {
      if (shape.type === 'freehand') drawFreehand(ctx, shape.points, shape.color, shape.thickness, shape.rotation);
      if (shape.type === 'square') drawSquare(ctx, shape.start, shape.end, shape.color, shape.thickness, shape.rotation);
      if (shape.type === 'circle') drawCircle(ctx, shape.start, shape.end, shape.color, shape.thickness, shape.rotation);
      if (shape.type === 'line') drawLine(ctx, shape.start, shape.end, shape.color, shape.thickness, shape.rotation);
      if (shape.type === 'arrow') drawArrow(ctx, shape.start, shape.end, shape.color, shape.thickness, shape.rotation);
      if (shape.type === 'text') drawText(ctx, shape);
    });
    
    // Draw all pins
    pins.forEach(pin => {
      drawPin(ctx, pin);
    });
    
    // Draw previews on top
    if (previewRect && currentTool === 'square') {
      drawPreviewSquare(previewRect.start, previewRect.end);
    }
    if (previewCircle && currentTool === 'circle') {
      drawPreviewCircle(previewCircle.start, previewCircle.end);
    }
    if (previewLine && currentTool === 'line') {
      drawPreviewLine(previewLine.start, previewLine.end);
    }
    if (previewArrow && currentTool === 'arrow') {
      drawPreviewArrow(previewArrow.start, previewArrow.end);
    }
  }, [previewRect, previewCircle, previewLine, previewArrow, color, currentTool, shapes, pins, image]);

  // Separate effect to handle text selection border without redrawing entire canvas
  useEffect(() => {
    if (!canvasRef.current || !selectedText) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Find the selected text shape
    const textShape = shapes.find(shape => shape.type === 'text' && shape.id === selectedText.id);
    if (!textShape) return;
    
    // Redraw only the text with selection border
    ctx.save();
    
    // Apply rotation if specified
    if (textShape.rotation && textShape.rotation !== 0) {
      ctx.translate(textShape.x, textShape.y);
      ctx.rotate((textShape.rotation * Math.PI) / 180);
      ctx.translate(-textShape.x, -textShape.y);
    }
    
    ctx.fillStyle = textShape.color || color;
    ctx.font = `${textShape.fontWeight || 'normal'} ${textShape.fontSize || 16}px sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    
    // Add text shadow for better visibility
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    
    // Split text by lines and draw each line
    const lines = textShape.text.split('\n');
    const lineHeight = (textShape.fontSize || 16) * 1.2;
    const maxWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
    const totalHeight = lines.length * lineHeight;
    
    lines.forEach((line, index) => {
      ctx.fillText(line, textShape.x, textShape.y + (index * lineHeight));
    });
    
    // Draw selection border
    ctx.restore();
    ctx.save();
    
    // Apply same rotation for selection border
    if (textShape.rotation && textShape.rotation !== 0) {
      ctx.translate(textShape.x, textShape.y);
      ctx.rotate((textShape.rotation * Math.PI) / 180);
      ctx.translate(-textShape.x, -textShape.y);
    }
    
    ctx.strokeStyle = '#1976d2';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.strokeRect(textShape.x - 4, textShape.y - 4, maxWidth + 8, totalHeight + 8);
    ctx.setLineDash([]);
    
    ctx.restore();
  }, [selectedText, shapes, color]);

  // Helper to draw a pin (numbered, colored, rotatable)
  const drawPin = (ctx, pin) => {
    ctx.save();
    ctx.translate(pin.x, pin.y);
    ctx.rotate((pin.rotation || 0) * Math.PI / 180);
    
    // Draw pin head (circle) - simple style
    ctx.beginPath();
    ctx.arc(0, 0, 8, 0, 2 * Math.PI);
    ctx.fillStyle = pin.color || '#008080';
    
    // Add simple shadow to pin head
    ctx.shadowColor = 'rgba(0,0,0,0.2)';
    ctx.shadowBlur = 3;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 1;
    ctx.fill();
    
    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    
    // Draw pin number
    ctx.fillStyle = '#fff';
    ctx.font = '600 10px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(pin.number || '', 0, 0);
    
    // Draw pin shaft
    ctx.beginPath();
    ctx.rect(-1, 8, 2, 8);
    ctx.fillStyle = pin.color || '#008080';
    ctx.fill();
    
    // Draw pin tip
    ctx.beginPath();
    ctx.moveTo(-1, 16); // Left point of triangle
    ctx.lineTo(1, 16);  // Right point of triangle
    ctx.lineTo(0, 19);  // Bottom sharp point
    ctx.closePath();
    ctx.fillStyle = pin.color || '#008080';
    ctx.fill();
    
    ctx.restore();
  };

  // Main render
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: height || 400, border: '1px solid #ccc', margin: '20px 0', position: 'relative', width: width || 600, height: height || 400 }}>
      {isVideo || image ? (
        <>
          {isVideo ? (
            <video src={videoUrl} autoPlay loop muted playsInline style={{ maxWidth: '100%', maxHeight: height || 400, display: 'block', position: 'absolute', left: 0, top: 0, width: width || 600, height: height || 400, zIndex: 1, objectFit: 'contain' }} />
          ) : (
            <img src={image} alt="Selected" style={{ maxWidth: '100%', maxHeight: height || 400, display: 'block', position: 'absolute', left: 0, top: 0, width: width || 600, height: height || 400, zIndex: 1, pointerEvents: 'none' }} />
          )}
          <canvas
            ref={canvasRef}
            width={width || 600}
            height={height || 400}
            style={{ position: 'absolute', left: 0, top: 0, zIndex: 2, pointerEvents: 'auto' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={handleCanvasClick}
          />
          {/* Pin clickable overlays for editing */}
          {pins.map(pin => {
            const isSelected = selectedPin && selectedPin.id === pin.id;
            return (
              <div
                key={pin.id}
                onClick={e => {
                  e.stopPropagation();
                  setSelectedPin(pin);
                  setShowPinModal(true);
                  // Do NOT change currentTool or placingPin here
                }}
                style={{
                  position: 'absolute',
                  left: pin.x - 10,
                  top: pin.y - 14,
                  width: 20,
                  height: 28,
                  zIndex: isSelected ? 10 : 3,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'none',
                  border: 'none',
                  pointerEvents: 'auto',
                  // boxShadow: isSelected ? '0 0 0 4px #e3f2fd, 0 2px 8px rgba(25,118,210,0.13)' : 'none',
                  borderRadius: 16,
                  transition: 'box-shadow 0.2s',
                  transform: `rotate(${pin.rotation || 0}deg)`,
                }}
                title={`Edit pin #${pin.number}`}
              >
                <PinIcon color={pin.color} number={pin.number} />
              </div>
            );
          })}
          {shapes.map((shape, idx) => {
            if (selectedShapeIndex === idx) {
              // Draw highlight overlay and controls in drag mode
              const center = getShapeCenter(shape);
              return (
                <>
                  {currentTool === 'drag' && (
                    <>
                      {/* X icon for delete - positioned at top */}
                      <div
                        style={{
                          position: 'absolute',
                          left: center.x + 20,
                          top: center.y - 25,
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'auto',
                          zIndex: 25,
                          fontSize: 12,
                          color: '#e53935',
                          background: 'rgba(255,255,255,0.9)',
                          borderRadius: '50%',
                          boxShadow: '0 1px 6px rgba(25,118,210,0.10)',
                          cursor: 'pointer',
                          border: '1px solid #e53935',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShapes(shapes => shapes.filter((_, index) => index !== idx));
                          setSelectedShapeIndex(null);
                        }}
                        title="Delete shape"
                      >
                        ✕
                      </div>
                      
                      {/* Finger icon for drag - positioned at center */}
                      <div
                        style={{
                          position: 'absolute',
                          left: center.x - 14,
                          top: center.y - 14,
                          width: 28,
                          height: 28,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'auto',
                          zIndex: 15,
                          fontSize: 24,
                          color: '#02414a',
                          background: 'rgba(255,255,255,0.85)',
                          borderRadius: '50%',
                          boxShadow: '0 1px 6px rgba(25,118,210,0.10)',
                          cursor: 'grab',
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          // Start dragging the shape
                          setDragOffset({
                            x: e.clientX - center.x,
                            y: e.clientY - center.y,
                          });
                        }}
                        title="Drag to move shape"
                      >
                        👆
                      </div>
                      
                      {/* Rotation slider - positioned at bottom */}
                      <div
                        style={{
                          position: 'absolute',
                          left: center.x - 50,
                          top: center.y + 20,
                          background: '#fff',
                          borderRadius: 8,
                          boxShadow: '0 2px 8px rgba(25,118,210,0.13)',
                          padding: '8px 14px',
                          zIndex: 20,
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      >
                        <span style={{ marginRight: 8 }}>⟳</span>
                        <input
                          type="range"
                          min={0}
                          max={360}
                          value={shapeRotation}
                          onChange={e => handleRotationChange(Number(e.target.value))}
                          style={{ width: 90, verticalAlign: 'middle' }}
                        />
                        <span style={{ marginLeft: 8 }}>{shapeRotation}&deg;</span>
                      </div>
                    </>
                  )}
                </>
              );
            }
            return null;
          })}
          {/* Text selection UI - similar to shape selection */}
          {selectedText && currentTool === 'drag' && (
            <>
              {/* X icon for delete - positioned higher with smaller size */}
              <div
                style={{
                  position: 'absolute',
                  left: selectedText.x + 20,
                  top: selectedText.y - 25,
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'auto',
                  zIndex: 25,
                  fontSize: 12,
                  color: '#e53935',
                  background: 'rgba(255,255,255,0.9)',
                  borderRadius: '50%',
                  boxShadow: '0 1px 6px rgba(25,118,210,0.10)',
                  cursor: 'pointer',
                  border: '1px solid #e53935',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleTextDelete(selectedText);
                }}
                title="Delete text"
              >
                ✕
              </div>
              
              {/* Finger icon for drag - positioned at center */}
              <div
                style={{
                  position: 'absolute',
                  left: selectedText.x - 14,
                  top: selectedText.y - 14,
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'auto',
                  zIndex: 15,
                  fontSize: 24,
                  color: '#02414a',
                  background: draggingText ? 'rgba(25,118,210,0.2)' : 'rgba(255,255,255,0.85)',
                  borderRadius: '50%',
                  boxShadow: '0 1px 6px rgba(25,118,210,0.10)',
                  cursor: draggingText ? 'grabbing' : 'grab',
                  transition: 'background 0.2s',
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setDraggingText(true);
                }}
                title="Drag to move text"
              >
                👆
              </div>
              
              {/* Rotation slider - positioned at bottom, only show when not dragging */}
              {!draggingText && (
                <div
                  style={{
                    position: 'absolute',
                    left: selectedText.x - 50,
                    top: selectedText.y + 20,
                    background: '#fff',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(25,118,210,0.13)',
                    padding: '8px 14px',
                    zIndex: 20,
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  <span style={{ marginRight: 8 }}>⟳</span>
                  <input
                    type="range"
                    min={0}
                    max={360}
                    value={selectedText.rotation || 0}
                    onChange={e => handleTextRotation(selectedText, Number(e.target.value))}
                    style={{ width: 90, verticalAlign: 'middle' }}
                  />
                  <span style={{ marginLeft: 8 }}>{selectedText.rotation || 0}&deg;</span>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <span style={{ color: '#888' }}>No media loaded</span>
      )}
    </div>
  );
});

export default Canvas;

// Helper for dropdown label
function dropdownLabel(value) {
  if (value === 'option1') return 'Option 1';
  if (value === 'option2') return 'Option 2';
  if (value === 'option3') return 'Option 3';
  return value;
}

// Helper: check if point is inside a shape (bounding box for simplicity)
function isPointInShape(point, shape) {
  if (shape.type === 'freehand') {
    // Check if point is near any segment
    for (let i = 1; i < shape.points.length; i++) {
      if (distanceToSegment(point, shape.points[i - 1], shape.points[i]) < 8) return true;
    }
    return false;
  }
  if (shape.type === 'square' || shape.type === 'line' || shape.type === 'arrow') {
    const minX = Math.min(shape.start.x, shape.end.x);
    const maxX = Math.max(shape.start.x, shape.end.x);
    const minY = Math.min(shape.start.y, shape.end.y);
    const maxY = Math.max(shape.start.y, shape.end.y);
    return point.x >= minX - 8 && point.x <= maxX + 8 && point.y >= minY - 8 && point.y <= maxY + 8;
  }
  if (shape.type === 'circle') {
    const cx = (shape.start.x + shape.end.x) / 2;
    const cy = (shape.start.y + shape.end.y) / 2;
    const rx = Math.abs(shape.end.x - shape.start.x) / 2;
    const ry = Math.abs(shape.end.y - shape.start.y) / 2;
    return Math.pow((point.x - cx) / (rx + 8), 2) + Math.pow((point.y - cy) / (ry + 8), 2) <= 1;
  }
  return false;
}

// Helper: get center of a shape
function getShapeCenter(shape) {
  if (shape.type === 'freehand') {
    // Use centroid
    const n = shape.points.length;
    const sum = shape.points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
    return { x: sum.x / n, y: sum.y / n };
  }
  if (shape.type === 'square' || shape.type === 'line' || shape.type === 'arrow' || shape.type === 'circle') {
    return {
      x: (shape.start.x + shape.end.x) / 2,
      y: (shape.start.y + shape.end.y) / 2,
    };
  }
  return { x: 0, y: 0 };
}

// Helper: move shape to new center
function moveShape(shape, pos, offset) {
  const center = getShapeCenter(shape);
  const dx = pos.x - offset.x - center.x;
  const dy = pos.y - offset.y - center.y;
  if (shape.type === 'freehand') {
    return {
      ...shape,
      points: shape.points.map(p => ({ x: p.x + dx, y: p.y + dy })),
    };
  }
  if (shape.type === 'square' || shape.type === 'line' || shape.type === 'arrow' || shape.type === 'circle') {
    return {
      ...shape,
      start: { x: shape.start.x + dx, y: shape.start.y + dy },
      end: { x: shape.end.x + dx, y: shape.end.y + dy },
    };
  }
  return shape;
}

// Helper: distance from point to segment
function distanceToSegment(p, v, w) {
  const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
  if (l2 === 0) return Math.hypot(p.x - v.x, p.y - v.y);
  let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (v.x + t * (w.x - v.x)), p.y - (v.y + t * (w.y - v.y)));
} 